import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import KnowledgeBaseSidebar, {
  KnowledgeBaseSidebarItem
} from '../Shared/KnowledgeBaseSidebar';
import '../Shared/Training.css';
import '../Shared/OrganizationalTraining.css';
import {
  ArrowIcon,
  DocumentIcon,
  LessonsIcon,
  ProgressIcon,
  TestsIcon,
  TimeIcon,
  VideoIcon
} from '../Shared/TrainingIcons';
import type { Course, Lesson, Category, TrainingType } from './types';
import { getLessonById, getLessons, mapApiLessonToOrganizationalCourse, mapApiLessonToCourse, markLessonComplete, markLessonIncomplete } from '../../../api/knowledgeApi';
import { useLesson } from '../Shared/useLesson';

type LessonRatings = Record<string, number>;
type LessonCompletionMap = Record<string, boolean>;
type QuizAnswerMap = Record<string, string | string[]>;
type QuizReviewOutcome = 'success' | 'failure';

const TrainingLesson: React.FC = () => {
  const navigate = useNavigate();
  const { chapterId, courseId, lessonId } = useParams<{
    chapterId?: string;
    courseId?: string;
    lessonId?: string;
  }>();

  // Определяем тип обучения по наличию chapterId
  const trainingType: TrainingType = chapterId ? 'profile' : 'organizational';
  const isProfile = trainingType === 'profile';
  const isOrganizational = trainingType === 'organizational';

  // Для профильного обучения используем useLesson хук
  const { lesson: apiLesson, loading: lessonLoading, error: lessonError } = useLesson(
    isProfile ? lessonId : undefined,
    isProfile
  );

  // Для организационного обучения загружаем весь курс
  const [course, setCourse] = useState<Course | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(!isProfile);
  const [error, setError] = useState<string | null>(null);
  const [dynamicCategories, setDynamicCategories] = useState<Category[]>([]);
  const [profileTags, setProfileTags] = useState<string[]>([]);
  const [imageModal, setImageModal] = useState<{ isOpen: boolean; src: string; alt: string }>({
    isOpen: false,
    src: '',
    alt: ''
  });
  const [imageZoom, setImageZoom] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Для профильного обучения создаем виртуальный курс из урока
  const virtualCourse: Course | undefined = useMemo(() => {
    if (!isProfile || !apiLesson || !courseId) return undefined;
    return {
      id: courseId,
      title: apiLesson.title || 'Урок',
      provider: 'AQMOL',
      providerLogo: 'autodesk',
      tags: [],
      lessonsCount: 1,
      testsCount: apiLesson.type === 'quiz' ? 1 : 0,
      duration: apiLesson.duration || '10 мин',
      completionPercent: 0,
      badgeText: 'API',
      badgeStatus: 'default',
      description: apiLesson.description || '',
      lessons: [apiLesson],
    };
  }, [apiLesson, courseId, isProfile]);

  // Загружаем курс из API по ID для организационного обучения
  useEffect(() => {
    if (!isOrganizational || !courseId) return;

    const loadCourse = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const apiLesson = await getLessonById(courseId);
        const mappedCourse = mapApiLessonToOrganizationalCourse(apiLesson, 'all');
        setCourse(mappedCourse);
      } catch (err) {
        console.error('Ошибка при загрузке курса:', err);
        setError('Не удалось загрузить курс. Попробуйте обновить страницу.');
      } finally {
        setIsLoading(false);
      }
    };

    loadCourse();
  }, [courseId, isOrganizational]);

  const activeCourse = isProfile ? virtualCourse : course;
  const lessons = useMemo(() => activeCourse?.lessons ?? [], [activeCourse]);
  const currentLessonIndex = useMemo(() => 
    Math.max(
      lessons.findIndex((l) => l.id === lessonId),
      0
    ),
    [lessons, lessonId]
  );
  const lesson: Lesson | undefined = isProfile 
    ? (apiLesson ?? undefined)
    : lessons[currentLessonIndex];

  const [ratings, setRatings] = useState<LessonRatings>({});
  const [completedLessons, setCompletedLessons] = useState<LessonCompletionMap>(() => {
    if (typeof window === 'undefined' || !courseId) {
      return {};
    }
    const storageKey = isProfile 
      ? `profile-${courseId}-completed-lessons`
      : `organizational-${courseId}-completed-lessons`;
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) {
      return {};
    }
    try {
      return JSON.parse(raw);
    } catch (error) {
      return {};
    }
  });
  const [acknowledged, setAcknowledged] = useState<boolean>(false);
  const [acknowledgedManuallyChanged, setAcknowledgedManuallyChanged] = useState<boolean>(false);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [quizQuestionIndex, setQuizQuestionIndex] = useState<number>(0);
  const [quizAnswers, setQuizAnswers] = useState<QuizAnswerMap>({});
  const [quizSubmitted, setQuizSubmitted] = useState<boolean>(false);
  const [showSubmissionModal, setShowSubmissionModal] = useState<boolean>(false);
  const [showExitConfirmModal, setShowExitConfirmModal] = useState<boolean>(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [reviewIteration, setReviewIteration] = useState<number>(() => {
    if (typeof window === 'undefined') {
      return 0;
    }
    const storageKey = isProfile 
      ? 'profileTrainingReviewIteration'
      : 'organizationalTrainingQuizIteration';
    const stored = Number(window.localStorage.getItem(storageKey) ?? '0');
    return Number.isFinite(stored) ? stored : 0;
  });
  const [reviewOutcome, setReviewOutcome] = useState<QuizReviewOutcome | null>(null);
  const [showReviewScreen, setShowReviewScreen] = useState<boolean>(false);

  // Инициализируем completedLessons из данных API при загрузке курса (только для организационного)
  useEffect(() => {
    if (isOrganizational && course) {
      const apiCompletedLessons: Record<string, boolean> = {};
      course.lessons.forEach((lessonItem) => {
        if (lessonItem.isCompleted) {
          apiCompletedLessons[lessonItem.id] = true;
        }
      });
      
      // Объединяем данные из API с данными из sessionStorage
      setCompletedLessons((prev) => {
        const merged = { ...apiCompletedLessons, ...prev };
        if (typeof window !== 'undefined' && courseId) {
          window.sessionStorage.setItem(
            `organizational-${courseId}-completed-lessons`,
            JSON.stringify(merged)
          );
        }
        return merged;
      });
    }
  }, [course, courseId, isOrganizational]);

  // Сброс состояния при смене урока
  useEffect(() => {
    setPageIndex(0);
    setQuizQuestionIndex(0);
    setQuizAnswers({});
    setQuizSubmitted(false);
    setShowSubmissionModal(false);
    setShowReviewScreen(false);
    setReviewOutcome(null);
    setPendingNavigation(null);
    setShowExitConfirmModal(false);
    setAcknowledgedManuallyChanged(false); // Сбрасываем флаг при смене урока
  }, [lesson?.id]);

  useEffect(() => {
    // Не перезаписываем значение, если пользователь только что изменил его вручную
    if (acknowledgedManuallyChanged) {
      return;
    }
    if (isProfile) {
      setAcknowledged(Boolean(completedLessons[lesson?.id ?? '']));
    } else {
      const isCompletedFromApi = lesson?.isCompleted ?? false;
      const isCompletedFromStorage = Boolean(completedLessons[lesson?.id ?? '']);
      setAcknowledged(isCompletedFromApi || isCompletedFromStorage);
    }
    setPageIndex(0);
  }, [lesson?.id, lesson?.isCompleted, completedLessons, isProfile, acknowledgedManuallyChanged]);

  // Загружаем категории для организационного обучения
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await getLessons({
          type: 'organizational',
          page: 1,
          page_size: 100,
        });
        const mappedCourses = response.results.map((apiLesson) =>
          mapApiLessonToOrganizationalCourse(apiLesson, 'all')
        );

        // Формируем категории из тегов курсов
        const allTags = new Set<string>();
        mappedCourses.forEach((courseItem) => {
          (courseItem.tags || []).forEach((tag: string) => allTags.add(tag));
        });

        const categoriesFromTags: Category[] = Array.from(allTags)
          .sort()
          .map((tag) => ({
            id: tag,
            name: tag,
            label: tag.charAt(0).toUpperCase() + tag.slice(1),
          }));

        setDynamicCategories(categoriesFromTags);
      } catch (err) {
        console.error('Ошибка при загрузке категорий организационного обучения:', err);
      }
    };

    loadCategories();
  }, []);

  // Загружаем теги для профильного обучения
  useEffect(() => {
    const loadProfileTags = async () => {
      try {
        const response = await getLessons({
          type: 'profile',
          page: 1,
          page_size: 100,
        });
        const mappedCourses = response.results.map((apiLesson) =>
          mapApiLessonToCourse(apiLesson)
        );
        const tagsSet = new Set<string>();
        mappedCourses.forEach((courseItem) => {
          (courseItem.tags || []).forEach((tag: string) => tagsSet.add(tag));
        });
        setProfileTags(Array.from(tagsSet).sort());
      } catch (err) {
        console.error('Ошибка при загрузке тегов профильного обучения:', err);
      }
    };

    loadProfileTags();
  }, []);

  useEffect(() => {
    // Не перезаписываем значение, если пользователь только что изменил его вручную
    if (acknowledgedManuallyChanged) {
      return;
    }
    // Используем is_completed из API или данные из sessionStorage
    const isCompletedFromApi = lesson?.isCompleted ?? false;
    const isCompletedFromStorage = Boolean(completedLessons[lesson?.id ?? '']);
    setAcknowledged(isCompletedFromApi || isCompletedFromStorage);
    setPageIndex(0);
  }, [lesson?.id, lesson?.isCompleted, completedLessons, acknowledgedManuallyChanged]);

  useEffect(() => {
    if (isProfile) {
      if (!lessonLoading && !apiLesson && !activeCourse) {
        navigate(`/knowledge-base/profile-training/${chapterId || 'all'}`);
      }
    } else {
      if (!isLoading && (!course || !lesson)) {
        navigate('/knowledge-base/organizational-training');
      }
    }
  }, [activeCourse, course, lesson, navigate, chapterId, lessonLoading, apiLesson, isLoading, isProfile]);

  // Обработчики для модального окна изображения
  const handleImageClick = useCallback((src: string, alt: string) => {
    setImageModal({ isOpen: true, src, alt });
    setImageZoom(1);
    setImagePosition({ x: 0, y: 0 });
  }, []);

  const handleCloseImageModal = useCallback(() => {
    setImageModal({ isOpen: false, src: '', alt: '' });
    setImageZoom(1);
    setImagePosition({ x: 0, y: 0 });
    setIsDragging(false);
  }, []);

  const handleZoomIn = useCallback(() => {
    setImageZoom((prev) => Math.min(prev + 0.25, 3));
  }, []);

  const handleZoomOut = useCallback(() => {
    setImageZoom((prev) => Math.max(prev - 0.25, 0.5));
  }, []);

  const handleResetZoom = useCallback(() => {
    setImageZoom(1);
    setImagePosition({ x: 0, y: 0 });
  }, []);

  // Обработчики для перетаскивания изображения
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - imagePosition.x,
      y: e.clientY - imagePosition.y
    });
  }, [imagePosition]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (isDragging) {
      setImagePosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Touch события для мобильных устройств
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - imagePosition.x,
        y: e.touches[0].clientY - imagePosition.y
      });
    }
  }, [imagePosition]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (isDragging && e.touches.length === 1) {
      e.preventDefault();
      setImagePosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y
      });
    }
  }, [isDragging, dragStart]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Обработка клавиши Escape для закрытия модального окна
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && imageModal.isOpen) {
        handleCloseImageModal();
      }
    };

    if (imageModal.isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Блокируем скролл страницы при открытом модальном окне
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [imageModal.isOpen, handleCloseImageModal]);

  // Обработка колесика мыши для зума
  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      if (!imageModal.isOpen) return;

      // Zoom при зажатом Ctrl/Cmd
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        if (event.deltaY < 0) {
          handleZoomIn();
        } else {
          handleZoomOut();
        }
      }
    };

    if (imageModal.isOpen) {
      window.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      window.removeEventListener('wheel', handleWheel);
    };
  }, [imageModal.isOpen, handleZoomIn, handleZoomOut]);

  // Обработка перетаскивания изображения
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Определяем активную категорию на основе тегов текущего курса (только для организационного)
  const activeCategoryId = useMemo(() => {
    if (isProfile) return chapterId || null;
    if (!course || !course.tags || course.tags.length === 0) {
      return null;
    }
    const courseTag = course.tags.find((tag) =>
      dynamicCategories.some((cat) => cat.id === tag)
    );
    return courseTag || null;
  }, [course, dynamicCategories, chapterId, isProfile]);

  const completionProgress = useMemo(() => {
    const total = lessons.length || 1;
    const completed = lessons.filter((item) => completedLessons[item.id]).length;
    const percent = Math.round((completed / total) * 100);
    return { completed, percent };
  }, [lessons, completedLessons]);

  const hasQuizAnswers = useMemo(
    () =>
      Object.values(quizAnswers).some((value) => {
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        return Boolean(value && String(value).trim().length > 0);
      }),
    [quizAnswers]
  );

  const recommendedCourse: Course | null = useMemo(() => {
    // Рекомендации будут загружаться из API в будущем
    return null;
  }, []);

  // Обработка состояния загрузки
  if (isProfile && lessonLoading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div>Загрузка урока...</div>
      </div>
    );
  }

  if (isOrganizational && isLoading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div>Загрузка урока...</div>
      </div>
    );
  }

  // Обработка ошибок
  if (isProfile && lessonError && !apiLesson) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: 'red' }}>
        <h3>Ошибка загрузки урока:</h3>
        <p>{lessonError.message}</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: '10px',
            padding: '8px 16px',
            background: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Обновить страницу
        </button>
      </div>
    );
  }

  if (isOrganizational && error && !course) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: 'red' }}>
        <h3>Ошибка загрузки курса:</h3>
        <p>{error}</p>
        <button
          onClick={() => navigate('/knowledge-base/organizational-training')}
          style={{
            marginTop: '10px',
            padding: '8px 16px',
            background: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Вернуться к списку курсов
        </button>
      </div>
    );
  }

  if (!activeCourse || !lesson) {
    return null;
  }

  const isQuiz = lesson.type === 'quiz';
  const quiz = lesson.quiz;
  const previousLesson = lessons[currentLessonIndex - 1];
  const nextLesson = lessons[currentLessonIndex + 1];
  const nextLessonIsQuiz = nextLesson?.type === 'quiz';
  const reviewOutcomes: QuizReviewOutcome[] = ['success', 'failure'];
  const shouldBlockNavigation = isQuiz && !quizSubmitted && hasQuizAnswers;
  const isLastLesson = lesson.isFinal;
  const isLastLessonInCourse = !nextLesson && !(isLastLesson && isOrganizational); // Последний урок в курсе (не финальный тест)
  // Для последнего урока в организационном обучении кнопка активна, если урок пройден
  // Для последнего урока в курсе кнопка активна, если урок пройден
  // Для обычных уроков кнопка активна, если есть следующий урок и текущий пройден
  // Для квизов кнопка активна, если пройден квиз (showReviewScreen && reviewOutcome)
  const isNextDisabled = (isLastLesson && isOrganizational) || isLastLessonInCourse
    ? !acknowledged
    : !nextLesson || (isQuiz ? !(showReviewScreen && reviewOutcome) : !acknowledged);
  const allLessonsCompleted = lessons.every((lessonItem) => completedLessons[lessonItem.id]);
  const showTestRow = Boolean(activeCourse?.quiz);
  const nextButtonLabel = isLastLesson && isOrganizational 
    ? 'Перейти к тесту' 
    : isLastLessonInCourse 
    ? 'Завершить курс' 
    : (nextLessonIsQuiz ? 'Перейти к тесту' : 'Следующий урок');
  const isQuizResultVisible = isQuiz && showReviewScreen && reviewOutcome;

  const requestNavigation = (path: string) => {
    if (shouldBlockNavigation) {
      setPendingNavigation(path);
      setShowExitConfirmModal(true);
      return;
    }
    navigate(path);
  };

  const handleNavigateLesson = (targetLesson: Lesson) => {
    if (isProfile) {
      requestNavigation(
        `/knowledge-base/profile-training/${chapterId}/${courseId}/lesson/${targetLesson.id}`
      );
    } else {
      requestNavigation(
        `/knowledge-base/organizational-training/${courseId}/lesson/${targetLesson.id}`
      );
    }
  };

  const handleAcknowledge = async (checked: boolean) => {
    setAcknowledged(checked);
    setAcknowledgedManuallyChanged(true); // Помечаем, что пользователь изменил значение вручную
    const storageKey = isProfile 
      ? `profile-${courseId}-completed-lessons`
      : `organizational-${courseId}-completed-lessons`;
    
    // Вызываем API для отметки урока
    if (courseId && lesson?.id) {
      try {
        if (checked) {
          await markLessonComplete(courseId, lesson.id);
        } else {
          await markLessonIncomplete(courseId, lesson.id);
        }
      } catch (error) {
        console.error('Ошибка при отметке урока:', error);
        // Продолжаем обновлять локальное состояние даже при ошибке API
      }
    }
    
    if (!checked) {
      setCompletedLessons((map) => {
        const next = { ...map };
        delete next[lesson.id];
        if (typeof window !== 'undefined' && courseId) {
          window.sessionStorage.setItem(storageKey, JSON.stringify(next));
        }
        return next;
      });
      return;
    }
    setCompletedLessons((map) => {
      const next = { ...map, [lesson.id]: true };
      if (typeof window !== 'undefined' && courseId) {
        window.sessionStorage.setItem(storageKey, JSON.stringify(next));
      }
      return next;
    });
  };

  const handleNextLesson = async () => {
    if (isQuiz) {
      if (!quizSubmitted || !reviewOutcome) {
        return;
      }
    } else if (!acknowledged) {
      return;
    }
    
    // Отмечаем текущий урок как пройденный через API
    if (courseId && lesson?.id) {
      try {
        await markLessonComplete(courseId, lesson.id);
      } catch (error) {
        console.error('Ошибка при отметке урока как пройденного:', error);
        // Продолжаем обновлять локальное состояние даже при ошибке API
      }
    }
    
    setCompletedLessons((map) => ({
      ...map,
      [lesson.id]: true
    }));
    
    // Если это последний урок в организационном обучении - переходим к тесту
    if (isOrganizational && lesson.isFinal) {
      if (course?.quiz) {
        navigate(`/knowledge-base/organizational-training/${courseId}/test`);
      } else {
        // eslint-disable-next-line no-alert
        window.alert('Тест для этого курса пока готовится.');
      }
      return;
    }
    
    // Если это последний урок в курсе (нет следующего) - переходим к списку курсов
    if (!nextLesson) {
      if (isProfile) {
        requestNavigation(`/knowledge-base/profile-training/${chapterId || 'all'}`);
      } else {
        requestNavigation('/knowledge-base/organizational-training');
      }
      return;
    }
    
    // Переход к следующему уроку
    if (isProfile) {
      requestNavigation(
        `/knowledge-base/profile-training/${chapterId}/${courseId}/lesson/${nextLesson.id}`
      );
    } else {
      requestNavigation(
        `/knowledge-base/organizational-training/${courseId}/lesson/${nextLesson.id}`
      );
    }
  };

  const handlePreviousLesson = () => {
    if (!previousLesson) {
      if (isProfile) {
        requestNavigation(`/knowledge-base/profile-training/${chapterId || 'all'}`);
      } else {
        requestNavigation('/knowledge-base/organizational-training');
      }
      return;
    }
    handleNavigateLesson(previousLesson);
  };

  const handleRateLesson = (rate: number) => {
    setRatings((map) => ({
      ...map,
      [lesson.id]: rate
    }));
  };

  const handleCloseSubmissionModal = () => {
    setShowSubmissionModal(false);
    if (isQuiz) {
      setShowReviewScreen(true);
    }
  };

  const handleRetakeQuiz = () => {
    setQuizQuestionIndex(0);
    setQuizAnswers({});
    setQuizSubmitted(false);
    setShowReviewScreen(false);
    setReviewOutcome(null);
    setShowSubmissionModal(false);
    setCompletedLessons((map) => {
      const next = { ...map };
      delete next[lesson.id];
      return next;
    });
  };

  const handleConfirmExitModal = () => {
    setShowExitConfirmModal(false);
    const target = pendingNavigation;
    setPendingNavigation(null);
    if (target) {
      setQuizAnswers({});
      setQuizSubmitted(false);
      navigate(target);
    }
  };

  const handleCancelExitModal = () => {
    setShowExitConfirmModal(false);
    setPendingNavigation(null);
  };

  const handleRecommendedCourseNavigation = () => {
    if (!recommendedCourse) {
      if (isProfile && chapterId) {
        requestNavigation(`/knowledge-base/profile-training/${chapterId}`);
      } else {
        requestNavigation('/knowledge-base/organizational-training');
      }
      return;
    }
    const course: Course = recommendedCourse;
    const firstLessonId = course.lessons[0]?.id;
    if (firstLessonId) {
      if (isProfile && chapterId) {
        requestNavigation(
          `/knowledge-base/profile-training/${chapterId}/${course.id}/lesson/${firstLessonId}`
        );
      } else {
        requestNavigation(
          `/knowledge-base/organizational-training/${course.id}/lesson/${firstLessonId}`
        );
      }
      return;
    }
    if (isProfile && chapterId) {
      requestNavigation(`/knowledge-base/profile-training/${chapterId}`);
    } else {
      requestNavigation('/knowledge-base/organizational-training');
    }
  };

  const renderRecommendedCourseCard = () => {
    if (!recommendedCourse) {
      return null;
    }
    const course: Course = recommendedCourse;
    const completionLabel =
      course.completionPercent === 100
        ? '100% завершено'
        : `${course.completionPercent}% завершено`;
    return (
      <div className="profile-training__quiz-recommendation-card">
        <div className="profile-training__quiz-recommendation-header">
          <span className="profile-training__quiz-recommendation-provider">
            {course.provider || 'AQMOL'}
          </span>
          <span className="profile-training__quiz-recommendation-status">
            {completionLabel}
          </span>
        </div>
        <button
          type="button"
          className="profile-training__quiz-recommendation-title"
          onClick={handleRecommendedCourseNavigation}
        >
          {course.title}
        </button>
        <div className="profile-training__quiz-recommendation-meta">
          <span>{course.lessonsCount} уроков</span>
          <span>{course.testsCount} тест</span>
          <span>{course.duration}</span>
        </div>
      </div>
    );
  };

  const renderQuizResult = () => {
    if (!reviewOutcome) {
      return null;
    }
    const isSuccess = reviewOutcome === 'success';
    const stats = isSuccess
      ? { percent: 75, correct: 'Правильных ответов: 4 из 5' }
      : { percent: 25, correct: 'Правильных ответов: 1 из 5' };

    return (
      <div className="profile-training__quiz-result">
        <div
          className={`profile-training__quiz-result-icon profile-training__quiz-result-icon--${
            isSuccess ? 'success' : 'failure'
          }`}
        />
        <h1 className="profile-training__quiz-result-title">
          {isSuccess ? 'Поздравляем! Вы успешно завершили курс:' : 'Тест не пройден'}
        </h1>
        <p
          className={`profile-training__quiz-result-percentage${
            isSuccess ? '' : ' profile-training__quiz-result-percentage--failure'
          }`}
        >
          {stats.percent}%
        </p>
        <p
          className={`profile-training__quiz-result-description${
            isSuccess ? '' : ' profile-training__quiz-result-description--failure'
          }`}
        >
          {stats.correct}
        </p>
        {isSuccess ? (
          <>
            <div className="profile-training__quiz-result-recommendation-title">
              Следующий рекомендуемый курс:
            </div>
            {renderRecommendedCourseCard()}
            <button
              type="button"
              className="profile-training__quiz-result-primary"
              onClick={handleRecommendedCourseNavigation}
            >
              Перейти к следующему курсу
            </button>
          </>
        ) : (
          <div className="profile-training__quiz-result-actions">
            <button
              type="button"
              className="profile-training__quiz-result-primary"
              onClick={() => {
                if (isProfile && chapterId) {
                  requestNavigation(`/knowledge-base/profile-training/${chapterId}`);
                } else {
                  requestNavigation('/knowledge-base/organizational-training');
                }
              }}
            >
              Вернуться к курсу
            </button>
            <button
              type="button"
              className="profile-training__quiz-result-secondary"
              onClick={handleRetakeQuiz}
            >
              Пройти тест заново
            </button>
          </div>
        )}
      </div>
    );
  };

  // Функция для преобразования YouTube URL в embed URL
  const getYouTubeEmbedUrl = (url: string): string | null => {
    if (!url) return null;
    
    // Проверяем, является ли это YouTube URL
    const youtubeRegex = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;
    const match = url.match(youtubeRegex);
    
    if (match && match[1]) {
      return `https://www.youtube.com/embed/${match[1]}`;
    }
    
    return null;
  };

  // Функция для определения типа файла
  const getFileType = (url: string): 'pdf' | 'image' | 'other' => {
    if (!url) return 'other';
    const extension = url.split('.').pop()?.toLowerCase() || '';
    if (extension === 'pdf') return 'pdf';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) return 'image';
    return 'other';
  };

  const renderLessonContent = () => {
    // Для профильного обучения: обработка квизов
    if (isProfile && lesson.type === 'quiz' && quiz) {
      const currentQuestion = quiz.questions[quizQuestionIndex];
      const totalQuestions = quiz.questions.length;
      const answeredCount = quiz.questions.filter((question) => {
        const answer = quizAnswers[question.id];
        if (question.type === 'text') {
          return Boolean(answer && String(answer).trim().length > 0);
        }
        if (Array.isArray(answer)) {
          return answer.length > 0;
        }
        return Boolean(answer);
      }).length;

      const handleChangeAnswer = (question: typeof currentQuestion, value: string) => {
        setQuizAnswers((prev) => {
          if (question.type === 'multiple') {
            const current = Array.isArray(prev[question.id])
              ? (prev[question.id] as string[])
              : [];
            const next = current.includes(value)
              ? current.filter((item) => item !== value)
              : [...current, value];
            return { ...prev, [question.id]: next };
          }
          return { ...prev, [question.id]: value };
        });
      };

      const handleTextAnswer = (questionId: string, value: string) => {
        setQuizAnswers((prev) => ({ ...prev, [questionId]: value }));
      };

      const handleSubmitQuiz = async () => {
        if (answeredCount < totalQuestions || quizSubmitted) {
          return;
        }
        setQuizSubmitted(true);
        
        // Отмечаем урок как пройденный через API
        if (courseId && lesson?.id) {
          try {
            await markLessonComplete(courseId, lesson.id);
          } catch (error) {
            console.error('Ошибка при отметке урока как пройденного:', error);
            // Продолжаем обновлять локальное состояние даже при ошибке API
          }
        }
        
        setCompletedLessons((map) => ({
          ...map,
          [lesson.id]: true
        }));
        const outcome = reviewOutcomes[reviewIteration % reviewOutcomes.length];
        setReviewOutcome(outcome);
        setReviewIteration((prev) => {
          const next = prev + 1;
          if (typeof window !== 'undefined') {
            const storageKey = isProfile 
              ? 'profileTrainingReviewIteration'
              : 'organizationalTrainingQuizIteration';
            window.localStorage.setItem(storageKey, String(next));
          }
          return next;
        });
        setShowSubmissionModal(true);
        setShowReviewScreen(false);
      };

      const isSubmitDisabled = answeredCount < totalQuestions || quizSubmitted;

      return (
        <div className="profile-training__lesson-quiz">
          <div className="profile-training__lesson-quiz-summary">
            <div className="profile-training__lesson-quiz-summary-item">
              <span className="profile-training__lesson-quiz-summary-icon">⏱</span>
              <div>
                <p className="profile-training__lesson-quiz-summary-label">Время выполнения</p>
                <p className="profile-training__lesson-quiz-summary-value">{quiz.duration}</p>
              </div>
            </div>
            <div className="profile-training__lesson-quiz-summary-item">
              <span className="profile-training__lesson-quiz-summary-icon">❓</span>
              <div>
                <p className="profile-training__lesson-quiz-summary-label">Вопросов</p>
                <p className="profile-training__lesson-quiz-summary-value">{quiz.questionsCount}</p>
              </div>
            </div>
            <div className="profile-training__lesson-quiz-summary-item">
              <span className="profile-training__lesson-quiz-summary-icon">🎯</span>
              <div>
                <p className="profile-training__lesson-quiz-summary-label">Условие прохождения</p>
                <p className="profile-training__lesson-quiz-summary-value">{quiz.passRequirement}</p>
              </div>
            </div>
          </div>

          <div className="profile-training__lesson-quiz-progress">
            <div
              className="profile-training__lesson-quiz-progress-bar"
              style={{ width: `${(answeredCount / totalQuestions) * 100}%` }}
            />
          </div>

          {currentQuestion && (
            <div className="profile-training__lesson-quiz-question">
              <h3>
                {currentQuestion.order}. {currentQuestion.text}
              </h3>
              {currentQuestion.type === 'text' ? (
                <textarea
                  className="profile-training__lesson-quiz-textarea"
                  placeholder={currentQuestion.placeholder ?? ''}
                  value={(quizAnswers[currentQuestion.id] as string) ?? ''}
                  onChange={(event) => handleTextAnswer(currentQuestion.id, event.target.value)}
                />
              ) : (
                <div className="profile-training__lesson-quiz-options">
                  {currentQuestion.options?.map((option) => {
                    const isMultiple = currentQuestion.type === 'multiple';
                    const answer = quizAnswers[currentQuestion.id];
                    const isChecked = isMultiple
                      ? Array.isArray(answer) && answer.includes(option.id)
                      : answer === option.id;

                    return (
                      <label key={option.id} className="profile-training__lesson-quiz-option">
                        <input
                          type={isMultiple ? 'checkbox' : 'radio'}
                          name={currentQuestion.id}
                          value={option.id}
                          checked={isChecked}
                          onChange={() => handleChangeAnswer(currentQuestion, option.id)}
                        />
                        <span>{option.label}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="profile-training__lesson-quiz-pagination">
            {quiz.questions.map((question, index) => {
              const answer = quizAnswers[question.id];
              const isAnswered =
                question.type === 'text'
                  ? Boolean(answer && String(answer).trim().length > 0)
                  : Array.isArray(answer)
                  ? answer.length > 0
                  : Boolean(answer);

              return (
                <button
                  key={question.id}
                  type="button"
                  className={`profile-training__lesson-quiz-page${
                    index === quizQuestionIndex ? ' profile-training__lesson-quiz-page--active' : ''
                  }${isAnswered ? ' profile-training__lesson-quiz-page--answered' : ''}`}
                  onClick={() => setQuizQuestionIndex(index)}
                >
                  {question.order}
                </button>
              );
            })}
          </div>

          <div className="profile-training__lesson-quiz-warning">
            <strong>Обратите внимание:</strong> после отправки теста редактирование ответов станет
            недоступным.
          </div>

          <button
            type="button"
            className="profile-training__lesson-quiz-submit"
            onClick={handleSubmitQuiz}
            disabled={isSubmitDisabled}
          >
            {quizSubmitted ? 'Отправлено' : 'Отправить на проверку'}
          </button>
        </div>
      );
    }

    // Для профильного обучения: обработка видео
    if (isProfile && lesson.type === 'video') {
      return (
        <div className="profile-training__lesson-video">
          <div className="profile-training__lesson-video-wrapper">
            {lesson.videoThumbnail ? (
              <img
                src={lesson.videoThumbnail}
                alt={lesson.title}
                className="profile-training__lesson-video-image"
              />
            ) : (
              <div className="profile-training__lesson-video-placeholder">
                <VideoIcon />
              </div>
            )}
            <button type="button" className="profile-training__lesson-video-play">
              ▶
            </button>
          </div>
        </div>
      );
    }

    // Приоритет 1: Если есть блоки контента, отображаем их ВСЕ в порядке order
    if (lesson.blocks && lesson.blocks.length > 0) {
      // Сортируем блоки по order на случай, если они пришли не отсортированными
      const sortedBlocks = [...lesson.blocks].sort((a, b) => (a.order || 0) - (b.order || 0));
      
      return (
        <div className="profile-training__lesson-content-blocks">
          {sortedBlocks.map((block) => {
            // Видео блок - отображаем через iframe
            if (block.type === 'video' && block.video_url) {
              const embedUrl = getYouTubeEmbedUrl(block.video_url);
              
              return (
                <div key={block.id} className="profile-training__lesson-video-block">
                  {embedUrl ? (
                    <div className="profile-training__lesson-video-embed">
                      <iframe
                        src={embedUrl}
                        title={block.text || lesson.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        style={{
                          width: '100%',
                          height: '500px',
                          border: 'none',
                          borderRadius: '8px'
                        }}
                      />
                    </div>
                  ) : (
                    <div className="profile-training__lesson-video-link">
                      <div className="profile-training__lesson-video-wrapper">
                        {block.media_url ? (
                          <img
                            src={block.media_url}
                            alt={lesson.title}
                            className="profile-training__lesson-video-image"
                          />
                        ) : lesson.videoThumbnail ? (
                          <img
                            src={lesson.videoThumbnail}
                            alt={lesson.title}
                            className="profile-training__lesson-video-image"
                          />
                        ) : (
                          <div className="profile-training__lesson-video-placeholder">
                            <span role="img" aria-label="Video">
                              🎬
                            </span>
                          </div>
                        )}
                        <button 
                          type="button" 
                          className="profile-training__lesson-video-play"
                          onClick={() => {
                            if (block.video_url) {
                              window.open(block.video_url, '_blank');
                            }
                          }}
                        >
                          ▶
                        </button>
                      </div>
                    </div>
                  )}
                  {block.text && (
                    <div className="profile-training__lesson-block-text">
                      <p style={{ whiteSpace: 'pre-wrap' }}>{block.text}</p>
                    </div>
                  )}
                </div>
              );
            }
            
            // Блок с медиа (изображения) - отображаем как картинку с возможностью зума
            if (block.type === 'media') {
              // Используем media_url или file_url для отображения изображения
              const imageUrl = block.media_url || block.file_url;
              if (imageUrl) {
                const fileType = getFileType(imageUrl);
                // Если это изображение, отображаем его с возможностью зума
                if (fileType === 'image') {
                  return (
                    <div key={block.id} className="profile-training__lesson-media-block">
                      <div className="profile-training__lesson-image-viewer">
                        <img
                          src={imageUrl}
                          alt={block.text || lesson.title}
                          onClick={() => handleImageClick(imageUrl, block.text || lesson.title)}
                          style={{
                            maxWidth: '100%',
                            height: 'auto',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'opacity 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = '0.9';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = '1';
                          }}
                        />
                      </div>
                      {block.text && (
                        <div className="profile-training__lesson-block-text">
                          <p style={{ whiteSpace: 'pre-wrap' }}>{block.text}</p>
                        </div>
                      )}
                    </div>
                  );
                }
              }
            }

            // Текстовый блок
            if (block.type === 'text') {
              return (
                <div key={block.id} className="profile-training__lesson-text-block">
                  <div className="profile-training__lesson-text-content">
                    {block.text && (
                      <p style={{ whiteSpace: 'pre-wrap' }}>{block.text}</p>
                    )}
                  </div>
                </div>
              );
            }

            // Файловый блок - если это изображение, отображаем как картинку с возможностью зума
            // Если не изображение - будет показан в разделе скачивания
            if (block.type === 'file' && block.file_url) {
              const fileType = getFileType(block.file_url);
              // Если это изображение, отображаем его как картинку в основном контенте
              if (fileType === 'image') {
                return (
                  <div key={block.id} className="profile-training__lesson-media-block">
                    <div className="profile-training__lesson-image-viewer">
                      <img
                        src={block.file_url}
                        alt={block.text || lesson.title}
                        onClick={() => handleImageClick(block.file_url!, block.text || lesson.title)}
                        style={{
                          maxWidth: '100%',
                          height: 'auto',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'opacity 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.opacity = '0.9';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.opacity = '1';
                        }}
                      />
                    </div>
                    {block.text && (
                      <div className="profile-training__lesson-block-text">
                        <p style={{ whiteSpace: 'pre-wrap' }}>{block.text}</p>
                      </div>
                    )}
                  </div>
                );
              }
              // Если не изображение, не отображаем здесь - будет показан в разделе скачивания
              return null;
            }
            
            return null;
          })}
        </div>
      );
    }

    // Приоритет 2: Если нет блоков, но есть material_file - отображаем его
    if (lesson.materialFile && typeof lesson.materialFile === 'string') {
      const fileType = getFileType(lesson.materialFile);
      
      return (
        <div className="profile-training__lesson-material">
          {fileType === 'pdf' ? (
            <div className="profile-training__lesson-pdf-viewer">
              <iframe
                src={lesson.materialFile}
                title={lesson.title}
                style={{
                  width: '100%',
                  height: '600px',
                  border: 'none',
                  borderRadius: '8px'
                }}
              />
            </div>
          ) : fileType === 'image' ? (
            <div className="profile-training__lesson-image-viewer">
              <img
                src={lesson.materialFile}
                alt={lesson.title}
                onClick={() => handleImageClick(lesson.materialFile!, lesson.title)}
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              />
            </div>
          ) : (
            <div className="profile-training__lesson-file-viewer">
              <iframe
                src={lesson.materialFile}
                title={lesson.title}
                style={{
                  width: '100%',
                  height: '600px',
                  border: 'none',
                  borderRadius: '8px'
                }}
              />
            </div>
          )}
        </div>
      );
    }

    // Приоритет 3: Если есть видео URL напрямую в уроке
    if (lesson.type === 'video' || lesson.videoUrl) {
      const embedUrl = lesson.videoUrl ? getYouTubeEmbedUrl(lesson.videoUrl) : null;
      
      return (
        <div className="profile-training__lesson-video">
          {embedUrl ? (
            <div className="profile-training__lesson-video-embed">
              <iframe
                src={embedUrl}
                title={lesson.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  width: '100%',
                  height: '500px',
                  border: 'none',
                  borderRadius: '8px'
                }}
              />
            </div>
          ) : (
          <div className="profile-training__lesson-video-wrapper">
            {lesson.videoThumbnail ? (
              <img
                src={lesson.videoThumbnail}
                alt={lesson.title}
                className="profile-training__lesson-video-image"
              />
            ) : (
              <div className="profile-training__lesson-video-placeholder">
                <span role="img" aria-label="Video">
                  🎬
                </span>
              </div>
            )}
              {lesson.videoUrl && (
                <button 
                  type="button" 
                  className="profile-training__lesson-video-play"
                  onClick={() => {
                    if (lesson.videoUrl) {
                      window.open(lesson.videoUrl, '_blank');
                    }
                  }}
                >
              ▶
            </button>
              )}
            </div>
          )}
          {lesson.description && (
            <div className="profile-training__lesson-block-text">
              <p style={{ whiteSpace: 'pre-wrap' }}>{lesson.description}</p>
            </div>
          )}
        </div>
      );
    }

    // Приоритет 4: Для документов используем pages или description
    const pages = lesson.pages ?? (lesson.description ? [lesson.description] : []);
    const totalPages = pages.length;

    if (totalPages === 0) {
      return (
        <div className="profile-training__lesson-document">
          <div className="profile-training__lesson-document-content">
            <p>Контент урока отсутствует</p>
          </div>
        </div>
      );
    }

    return (
      <div className="profile-training__lesson-document">
        <header className="profile-training__lesson-document-header">
          <span>{lesson.title}</span>
          {totalPages > 1 && (
          <span>
            Страница {pageIndex + 1}/{totalPages}
          </span>
          )}
        </header>
        <div className="profile-training__lesson-document-viewer">
          {totalPages > 1 && (
          <button
            type="button"
            className="profile-training__lesson-document-nav"
            onClick={() => setPageIndex((index) => Math.max(index - 1, 0))}
            disabled={pageIndex === 0}
          >
            ‹
          </button>
          )}
          <div className="profile-training__lesson-document-page">
            <div className="profile-training__lesson-document-content">
              <p style={{ whiteSpace: 'pre-wrap' }}>{pages[pageIndex]}</p>
            </div>
          </div>
          {totalPages > 1 && (
          <button
            type="button"
            className="profile-training__lesson-document-nav"
            onClick={() => setPageIndex((index) => Math.min(index + 1, totalPages - 1))}
            disabled={pageIndex === totalPages - 1}
          >
            ›
          </button>
          )}
        </div>
      </div>
    );
  };

  const organizationalSidebarItems: KnowledgeBaseSidebarItem[] = [
    {
      id: 'all',
      label: 'Все разделы',
      onClick: () => navigate('/knowledge-base/organizational-training'),
      isActive: isOrganizational && !activeCategoryId
    },
    ...dynamicCategories.map((category: Category) => ({
      id: category.id,
      label: category.label || category.name,
      onClick: () => navigate(`/knowledge-base/organizational-training?category=${category.id}`),
      isActive: isOrganizational && activeCategoryId === category.id
    }))
  ];

  const profileSidebarItems: KnowledgeBaseSidebarItem[] = [
    {
      id: 'all',
      label: 'Все разделы',
      onClick: () => navigate('/knowledge-base/profile-training/all'),
      isActive: isProfile && activeCategoryId === 'all'
    },
    ...profileTags.map((tag) => ({
      id: tag,
      label: tag.charAt(0).toUpperCase() + tag.slice(1),
      onClick: () => navigate(`/knowledge-base/profile-training/${tag}`),
      isActive: isProfile && activeCategoryId === tag
    }))
  ];

  const trainingTitle = isProfile ? 'Профильное обучение' : 'Организационное обучение';
  const trainingBasePath = isProfile 
    ? `/knowledge-base/profile-training/${chapterId || 'all'}`
    : '/knowledge-base/organizational-training';

  return (
    <div className={`profile-training ${isOrganizational ? 'organizational-training' : ''}`}>
      <div className={`profile-training__container ${isOrganizational ? 'organizational-training__container' : ''} profile-training__container--lesson`}>
        <KnowledgeBaseSidebar
          activeSection={isProfile ? 'profile' : 'organizational'}
          organizationalItems={organizationalSidebarItems}
          profileItems={profileSidebarItems}
        />

        <main className={`profile-training__main profile-training__main--lesson${isQuizResultVisible ? ' profile-training__main--quiz-result' : ''}`}>
          {isQuizResultVisible ? (
            renderQuizResult()
          ) : (
            <>
              <div className="profile-training__breadcrumbs">
                <button
                  type="button"
                  className="profile-training__breadcrumb-item profile-training__breadcrumb-link"
                  onClick={() => requestNavigation('/knowledge-base')}
                >
                  Главная
                </button>
                <span className="profile-training__breadcrumb-separator">›</span>
                <button
                  type="button"
                  className="profile-training__breadcrumb-item profile-training__breadcrumb-link"
                  onClick={() => requestNavigation(trainingBasePath)}
                >
                  {trainingTitle}
                </button>
                {isProfile && chapterId && chapterId !== 'all' && (
                  <>
                    <span className="profile-training__breadcrumb-separator">›</span>
                    <button
                      type="button"
                      className="profile-training__breadcrumb-item profile-training__breadcrumb-link"
                      onClick={() => requestNavigation(`/knowledge-base/profile-training/${chapterId}`)}
                    >
                      {chapterId}
                    </button>
                  </>
                )}
                <span className="profile-training__breadcrumb-separator">›</span>
                <span className="profile-training__breadcrumb-item">{activeCourse?.title}</span>
                <span className="profile-training__breadcrumb-separator">›</span>
                <span className="profile-training__breadcrumb-item">{lesson.title}</span>
              </div>

              <div className="profile-training__lesson-header">
                <div className="profile-training__lesson-back">
                  <button
                    className="profile-training__back-button"
                    onClick={() => requestNavigation(trainingBasePath)}
                  >
                    <span className="profile-training__back-arrow">
                      <ArrowIcon />
                    </span>
                  </button>
                  <div className="profile-training__lesson-breadcrumb">
                    <span className="profile-training__lesson-course">{activeCourse?.title}</span>
                    <span className="profile-training__lesson-status">
                      {completionProgress.percent}% завершено ({completionProgress.completed}/{lessons.length})
                    </span>
                  </div>
                </div>
                <div className="profile-training__lesson-metrics">
                  <div className="profile-training__lesson-metric">
                    <LessonsIcon />
                    <span>{activeCourse?.lessonsCount || lessons.length} уроков</span>
                  </div>
                  <div className="profile-training__lesson-metric">
                    <TestsIcon />
                    <span>{activeCourse?.testsCount || 0} тест</span>
                  </div>
                  <div className="profile-training__lesson-metric">
                    <TimeIcon />
                    <span>{activeCourse?.duration || '10 мин'}</span>
                  </div>
                  <div className="profile-training__lesson-metric">
                    <ProgressIcon />
                    <span>{completionProgress.percent}%</span>
                  </div>
                </div>
              </div>

          <section className="profile-training__lesson-content">
            <header className="profile-training__lesson-content-header">
              <div>
                <h1 className="profile-training__lesson-title">{lesson.title}</h1>
                {isProfile && lesson.description && (
                  <p className="profile-training__lesson-description">{lesson.description}</p>
                )}
              </div>
              <div className="profile-training__lesson-meta">
                <TimeIcon />
                <span>{lesson.duration}</span>
                {lesson.difficulty && (
                  <>
                    <span className="profile-training__lesson-dot">•</span>
                    <span>Сложность: {lesson.difficulty}</span>
                  </>
                )}
                <span className="profile-training__lesson-dot">•</span>
                <span>{lesson.updatedAt}</span>
              </div>
            </header>

            {renderLessonContent()}

            {/* Файлы из blocks - материалы для скачивания */}
            {(() => {
              // В "Материалах для скачивания" показываем ТОЛЬКО файлы из blocks типа "file"
              const filesToDownload: Array<{
                id: number | string;
                fileName: string;
                fileUrl: string;
                text?: string;
              }> = [];

              // Добавляем файлы из blocks (все блоки типа "file")
              lesson.blocks?.forEach((block) => {
                if (block.type === 'file' && block.file_url) {
                  filesToDownload.push({
                    id: block.id,
                    fileName: block.file_url.split('/').pop() || 'file',
                    fileUrl: block.file_url,
                    text: block.text,
                  });
                }
              });

              // Показываем раздел только если есть файлы для скачивания
              if (filesToDownload.length === 0) {
                return null;
              }

              return (
                <div className="profile-training__lesson-attachments">
                  <h3>Материалы для скачивания</h3>
                  <div className="profile-training__lesson-attachments-list">
                    {filesToDownload.map((file) => (
                      <div key={file.id} className="profile-training__lesson-attachment">
                        <div className="profile-training__lesson-attachment-icon">📄</div>
                        <div className="profile-training__lesson-attachment-info">
                          <span className="profile-training__lesson-attachment-title">
                            {file.fileName}
                          </span>
                          {file.text && (
                            <span className="profile-training__lesson-attachment-meta">
                              {file.text}
                            </span>
                          )}
                        </div>
                        <button 
                          type="button" 
                          className="profile-training__lesson-attachment-download"
                          onClick={() => {
                            if (file.fileUrl) {
                              window.open(file.fileUrl, '_blank');
                            } else {
                              console.log('Скачать файл:', file.fileName);
                            }
                          }}
                        >
                          Скачать
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {!isQuiz && (
              <>
                {(() => {
                  // Получаем список всех URL файлов из blocks типа "file" для исключения из attachments
                  const allBlockFileUrls = new Set(
                    lesson.blocks
                      ?.filter(block => block.type === 'file' && block.file_url)
                      .map(block => block.file_url!)
                      .filter((url): url is string => Boolean(url)) || []
                  );

                  // Фильтруем attachments:
                  // 1. Исключаем material_file (он уже отображается в основном контенте или в материалах для скачивания)
                  // 2. Исключаем ВСЕ файлы из blocks типа "file" (они уже отображаются в "Материалах для скачивания")
                  const attachmentsToShow = lesson.attachments.filter((attachment) => {
                    // Исключаем material_file по имени файла
                    if (lesson.materialFile && typeof lesson.materialFile === 'string') {
                      const materialFileName = lesson.materialFile.split('/').pop() || '';
                      if (attachment.title === materialFileName) {
                        return false;
                      }
                    }

                    // Исключаем ВСЕ файлы, которые уже есть в blocks типа "file"
                    const attachmentFileName = attachment.title;
                    const isInFileBlocks = Array.from(allBlockFileUrls).some(url => {
                      const blockFileName = url.split('/').pop() || '';
                      return blockFileName === attachmentFileName;
                    });

                    return !isInFileBlocks;
                  });

                  // Показываем секцию только если есть файлы для отображения
                  if (attachmentsToShow.length === 0) {
                    return null;
                  }

                  return (
                    <div className="profile-training__lesson-attachments">
                      <h3>Прикреплённые файлы</h3>
                      <div className="profile-training__lesson-attachments-list">
                        {attachmentsToShow.map((attachment) => (
                          <div key={attachment.id} className="profile-training__lesson-attachment">
                            <div className="profile-training__lesson-attachment-icon">
                              <DocumentIcon />
                            </div>
                            <div className="profile-training__lesson-attachment-info">
                              <span className="profile-training__lesson-attachment-title">{attachment.title}</span>
                              <span className="profile-training__lesson-attachment-meta">{attachment.size}</span>
                            </div>
                            <button type="button" className="profile-training__lesson-attachment-download">
                              Скачать
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                <div className="profile-training__lesson-feedback">
                  <div className="profile-training__lesson-rating">
                    <span>Как вы оцениваете этот урок?</span>
                    <div className="profile-training__lesson-rating-stars">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          className={`profile-training__lesson-rating-star${
                            (ratings[lesson.id] ?? 0) >= star ? ' profile-training__lesson-rating-star--active' : ''
                          }`}
                          onClick={() => handleRateLesson(star)}
                        >
                          ★
                        </button>
                      ))}
                    </div>
                  </div>
                  <label className="profile-training__lesson-acknowledge">
                    <input
                      type="checkbox"
                      checked={acknowledged}
                      onChange={(event) => handleAcknowledge(event.target.checked)}
                    />
                    <span>Я ознакомлен(а) с содержанием и подтверждаю прочтение</span>
                  </label>
                </div>
              </>
            )}

            <footer className="profile-training__lesson-footer">
              <button
                type="button"
                className="profile-training__lesson-nav profile-training__lesson-nav--prev"
                onClick={handlePreviousLesson}
                disabled={!previousLesson}
              >
                Назад
              </button>
              <button
                type="button"
                className="profile-training__lesson-nav profile-training__lesson-nav--next"
                onClick={handleNextLesson}
                disabled={isNextDisabled}
              >
                {nextButtonLabel}
                {!isLastLesson && !isLastLessonInCourse && <span className="profile-training__lesson-nav-arrow">→</span>}
              </button>
            </footer>
          </section>
            </>
          )}
        </main>

        {!isQuizResultVisible && (
          <aside className="profile-training__lesson-sidebar">
            <div className="profile-training__lesson-sidebar-card">
              <div className="profile-training__lesson-sidebar-header">
                <h3>{activeCourse?.title}</h3>
                <span
                  className={`profile-training__course-badge profile-training__course-badge--${completionProgress.percent === 100 ? 'completed' : 'default'}`}
                >
                  {completionProgress.percent === 100 ? 'Завершено' : 'В процессе'}
                </span>
              </div>

              <ul className="profile-training__lesson-list">
                {lessons.map((courseLesson) => {
                  const isCurrent = courseLesson.id === lesson.id;
                  const isCompleted =
                    completedLessons[courseLesson.id] ||
                    (courseLesson.type === 'quiz' && quizSubmitted);
                  return (
                    <li
                      key={courseLesson.id}
                      className={`profile-training__lesson-list-item${
                        isCurrent ? ' profile-training__lesson-list-item--active' : ''
                      }${isCompleted ? ' profile-training__lesson-list-item--completed' : ''}`}
                    >
                      <button
                        type="button"
                        onClick={() => handleNavigateLesson(courseLesson)}
                        className="profile-training__lesson-list-button"
                      >
                        <div className="profile-training__lesson-list-order">{courseLesson.order}</div>
                        <div className="profile-training__lesson-list-content">
                          <span className="profile-training__lesson-list-title">{courseLesson.title}</span>
                          <span className="profile-training__lesson-list-meta">{courseLesson.duration}</span>
                        </div>
                        <div className="profile-training__lesson-list-status">
                          {isCompleted ? '✓' : isCurrent ? '…' : ''}
                        </div>
                      </button>
                    </li>
                  );
                })}
                {showTestRow && isOrganizational && (
                  <li
                    className={`profile-training__lesson-list-item${
                      allLessonsCompleted
                        ? ' profile-training__lesson-list-item--completed'
                        : ''
                    }`}
                  >
                    <button
                      type="button"
                      className="profile-training__lesson-list-button"
                      onClick={() => navigate(`/knowledge-base/organizational-training/${courseId}/test`)}
                      disabled={!allLessonsCompleted}
                    >
                      <div className="profile-training__lesson-list-order">Т</div>
                      <div className="profile-training__lesson-list-content">
                        <span className="profile-training__lesson-list-title">Тестирование / Итоговое задание</span>
                        <span className="profile-training__lesson-list-meta">{activeCourse?.quiz?.duration ?? '—'}</span>
                      </div>
                      <div className="profile-training__lesson-list-status">
                        {allLessonsCompleted ? '→' : ''}
                      </div>
                    </button>
                  </li>
                )}
              </ul>
            </div>
          </aside>
        )}
      </div>

      {/* Модальное окно для полноэкранного просмотра изображений */}
      {imageModal.isOpen && (
        <div className="profile-training__image-modal" onClick={handleCloseImageModal}>
          <div className="profile-training__image-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="profile-training__image-modal-header">
              <div className="profile-training__image-modal-controls">
                <button
                  type="button"
                  className="profile-training__image-modal-button"
                  onClick={handleZoomOut}
                  disabled={imageZoom <= 0.5}
                  title="Уменьшить"
                >
                  −
                </button>
                <button
                  type="button"
                  className="profile-training__image-modal-button"
                  onClick={handleResetZoom}
                  title="Сбросить масштаб"
                >
                  {Math.round(imageZoom * 100)}%
                </button>
                <button
                  type="button"
                  className="profile-training__image-modal-button"
                  onClick={handleZoomIn}
                  disabled={imageZoom >= 3}
                  title="Увеличить"
                >
                  +
                </button>
              </div>
              <button
                type="button"
                className="profile-training__image-modal-close"
                onClick={handleCloseImageModal}
                title="Закрыть"
              >
                ×
              </button>
            </div>
            <div className="profile-training__image-modal-body">
              <img
                src={imageModal.src}
                alt={imageModal.alt}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  transform: `scale(${imageZoom}) translate(${imagePosition.x / imageZoom}px, ${imagePosition.y / imageZoom}px)`,
                  transition: isDragging ? 'none' : 'transform 0.2s ease',
                  cursor: isDragging ? 'grabbing' : 'grab',
                  userSelect: 'none',
                  WebkitUserSelect: 'none'
                }}
                draggable={false}
              />
            </div>
          </div>
        </div>
      )}

      {/* Модальные окна для квизов (профильное обучение) */}
      {showSubmissionModal && (
        <div className="profile-training__modal-overlay">
          <div className="profile-training__modal">
            <div className="profile-training__modal-icon profile-training__modal-icon--success" />
            <h3 className="profile-training__modal-title">Тест отправлен на проверку</h3>
            <p className="profile-training__modal-description">
              Ваши ответы успешно сохранены и отправлены на проверку. Результаты появятся после проверки
              администратором.
            </p>
            <div className="profile-training__modal-recommendation">
              <span className="profile-training__modal-recommendation-label">
                Следующий рекомендуемый курс:
              </span>
              {renderRecommendedCourseCard()}
            </div>
            <button
              type="button"
              className="profile-training__modal-primary"
              onClick={handleCloseSubmissionModal}
            >
              Понятно
            </button>
          </div>
        </div>
      )}

      {showExitConfirmModal && (
        <div className="profile-training__modal-overlay">
          <div className="profile-training__modal profile-training__modal--compact">
            <div className="profile-training__modal-icon profile-training__modal-icon--warning" />
            <h3 className="profile-training__modal-title">Вы не завершили тест.</h3>
            <p className="profile-training__modal-description">
              При выходе ответы могут быть потеряны.
            </p>
            <div className="profile-training__modal-actions">
              <button
                type="button"
                className="profile-training__modal-secondary"
                onClick={handleConfirmExitModal}
              >
                Выйти без сохранения
              </button>
              <button
                type="button"
                className="profile-training__modal-primary"
                onClick={handleCancelExitModal}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingLesson;

